/**
 * TouchController — Reusable virtual gamepad for mobile/touch devices.
 *
 * Features:
 *   - Floating virtual joystick (appears where you touch)
 *   - Configurable action buttons
 *   - Multi-touch support (joystick + buttons simultaneously)
 *   - Self-contained: creates its own DOM elements and injects scoped CSS
 *   - Single-file, zero dependencies — copy to any project
 *
 * Usage:
 *   const tc = new TouchController({
 *     buttons: [
 *       { id: 'jump', label: 'JUMP', color: '#44ff44' },
 *       { id: 'fire', label: 'FIRE', color: '#ff4444' },
 *     ],
 *   });
 *   // In game loop:
 *   const state = tc.getState();
 *   // state.joystick.x  → -1 (left) to 1 (right)
 *   // state.joystick.y  → -1 (up/forward) to 1 (down/backward)
 *   // state.buttons.jump → true/false
 */

// ─── Public types ────────────────────────────────────────────

export interface TouchButton {
  id: string;
  label: string;
  color?: string;
}

export interface TouchControllerOptions {
  /** Action buttons to show (default: none) */
  buttons?: TouchButton[];
  /** Which side the joystick appears on (default: 'left') */
  joystickSide?: 'left' | 'right';
  /** Opacity of controls (default: 0.55) */
  opacity?: number;
  /** Joystick max radius in px (default: 60) */
  joystickRadius?: number;
  /** Deadzone as fraction of radius — below this, output is 0 (default: 0.15) */
  deadzone?: number;
  /** Auto-show when touch device is detected (default: true) */
  autoShow?: boolean;
}

export interface TouchState {
  /** Joystick axes, each normalised from -1 to 1 */
  joystick: { x: number; y: number };
  /** Active state of each button by id */
  buttons: Record<string, boolean>;
}

// ─── Implementation ──────────────────────────────────────────

const CSS_PREFIX = 'tc';

export class TouchController {
  private container: HTMLDivElement;
  private joystickZone: HTMLDivElement;
  private joystickBase: HTMLDivElement;
  private joystickThumb: HTMLDivElement;
  private buttonEls: Map<string, HTMLDivElement> = new Map();
  private styleEl: HTMLStyleElement;

  // State
  private joyTouchId: number | null = null;
  private joyOrigin = { x: 0, y: 0 };
  private joyDelta = { x: 0, y: 0 };
  private btnTouchIds: Map<string, number> = new Map(); // buttonId → touchIdentifier
  private buttonStates: Record<string, boolean> = {};

  // Config
  private radius: number;
  private deadzone: number;
  private side: 'left' | 'right';
  private visible = false;

  constructor(options: TouchControllerOptions = {}) {
    const {
      buttons = [],
      joystickSide = 'left',
      opacity = 0.55,
      joystickRadius = 60,
      deadzone = 0.15,
      autoShow = true,
    } = options;

    this.radius = joystickRadius;
    this.deadzone = deadzone;
    this.side = joystickSide;

    // Initialise button states
    for (const btn of buttons) {
      this.buttonStates[btn.id] = false;
    }

    // ── Inject CSS ──
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = this.buildCSS(opacity, buttons);
    document.head.appendChild(this.styleEl);

    // ── Build DOM ──
    this.container = document.createElement('div');
    this.container.className = `${CSS_PREFIX}-container`;

    // Joystick zone (covers one half of the screen)
    this.joystickZone = document.createElement('div');
    this.joystickZone.className = `${CSS_PREFIX}-joy-zone ${CSS_PREFIX}-joy-zone--${joystickSide}`;

    this.joystickBase = document.createElement('div');
    this.joystickBase.className = `${CSS_PREFIX}-joy-base`;

    this.joystickThumb = document.createElement('div');
    this.joystickThumb.className = `${CSS_PREFIX}-joy-thumb`;

    this.joystickBase.appendChild(this.joystickThumb);
    this.joystickZone.appendChild(this.joystickBase);
    this.container.appendChild(this.joystickZone);

    // Buttons (on the opposite side)
    if (buttons.length > 0) {
      const btnSide = joystickSide === 'left' ? 'right' : 'left';
      const btnContainer = document.createElement('div');
      btnContainer.className = `${CSS_PREFIX}-btn-container ${CSS_PREFIX}-btn-container--${btnSide}`;

      for (const btn of buttons) {
        const el = document.createElement('div');
        el.className = `${CSS_PREFIX}-btn`;
        el.dataset.btnId = btn.id;
        el.textContent = btn.label;
        if (btn.color) {
          el.style.borderColor = btn.color;
          el.style.color = btn.color;
        }
        btnContainer.appendChild(el);
        this.buttonEls.set(btn.id, el);
      }

      this.container.appendChild(btnContainer);
    }

    document.body.appendChild(this.container);

    // ── Bind touch events ──
    this.bindEvents();

    // ── Auto-show ──
    if (autoShow && this.isTouchDevice()) {
      this.show();
    }
  }

  // ─── Public API ──────────────────────────────────────────

  /** Get the current input state. Call once per frame. */
  getState(): TouchState {
    let x = this.joyDelta.x / this.radius;
    let y = this.joyDelta.y / this.radius;

    // Clamp to unit circle
    const mag = Math.sqrt(x * x + y * y);
    if (mag > 1) {
      x /= mag;
      y /= mag;
    }

    // Apply deadzone
    if (Math.abs(x) < this.deadzone) x = 0;
    if (Math.abs(y) < this.deadzone) y = 0;

    return {
      joystick: { x, y },
      buttons: { ...this.buttonStates },
    };
  }

  /** Show the touch controls */
  show(): void {
    this.visible = true;
    this.container.style.display = '';
  }

  /** Hide the touch controls */
  hide(): void {
    this.visible = false;
    this.container.style.display = 'none';
    this.resetJoystick();
    this.resetAllButtons();
  }

  /** Check if controls are currently visible */
  isVisible(): boolean {
    return this.visible;
  }

  /** Detect touch-capable device */
  isTouchDevice(): boolean {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      matchMedia('(pointer: coarse)').matches
    );
  }

  /** Clean up all DOM and event listeners */
  dispose(): void {
    this.container.remove();
    this.styleEl.remove();
    // Touch listeners are on the container which is now removed,
    // so they'll be garbage-collected.
  }

  // ─── Event handling ──────────────────────────────────────

  private bindEvents(): void {
    // Use non-passive listeners so we can preventDefault
    const opts: AddEventListenerOptions = { passive: false };

    this.joystickZone.addEventListener('touchstart', this.onJoyTouchStart, opts);
    this.joystickZone.addEventListener('touchmove', this.onJoyTouchMove, opts);
    this.joystickZone.addEventListener('touchend', this.onJoyTouchEnd, opts);
    this.joystickZone.addEventListener('touchcancel', this.onJoyTouchEnd, opts);

    // Button events are on each button
    for (const [id, el] of this.buttonEls) {
      el.addEventListener('touchstart', (e) => this.onBtnTouchStart(e, id), opts);
      el.addEventListener('touchend', (e) => this.onBtnTouchEnd(e, id), opts);
      el.addEventListener('touchcancel', (e) => this.onBtnTouchEnd(e, id), opts);
    }
  }

  // ── Joystick ──

  private onJoyTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (this.joyTouchId !== null) return; // already tracking a touch

    const touch = e.changedTouches[0];
    this.joyTouchId = touch.identifier;
    this.joyOrigin = { x: touch.clientX, y: touch.clientY };
    this.joyDelta = { x: 0, y: 0 };

    // Position the joystick at the touch point
    this.joystickBase.style.left = `${touch.clientX}px`;
    this.joystickBase.style.top = `${touch.clientY}px`;
    this.joystickBase.classList.add(`${CSS_PREFIX}-joy-base--active`);

    this.joystickThumb.style.transform = 'translate(-50%, -50%)';
  };

  private onJoyTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (this.joyTouchId === null) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier !== this.joyTouchId) continue;

      let dx = touch.clientX - this.joyOrigin.x;
      let dy = touch.clientY - this.joyOrigin.y;

      // Clamp to radius
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > this.radius) {
        dx = (dx / dist) * this.radius;
        dy = (dy / dist) * this.radius;
      }

      this.joyDelta = { x: dx, y: dy };
      this.joystickThumb.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      break;
    }
  };

  private onJoyTouchEnd = (e: TouchEvent): void => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.joyTouchId) {
        this.resetJoystick();
        break;
      }
    }
  };

  private resetJoystick(): void {
    this.joyTouchId = null;
    this.joyDelta = { x: 0, y: 0 };
    this.joystickBase.classList.remove(`${CSS_PREFIX}-joy-base--active`);
    this.joystickThumb.style.transform = 'translate(-50%, -50%)';
  }

  // ── Buttons ──

  private onBtnTouchStart(e: TouchEvent, id: string): void {
    e.preventDefault();
    const touch = e.changedTouches[0];
    this.btnTouchIds.set(id, touch.identifier);
    this.buttonStates[id] = true;
    this.buttonEls.get(id)?.classList.add(`${CSS_PREFIX}-btn--active`);
  }

  private onBtnTouchEnd(e: TouchEvent, id: string): void {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.btnTouchIds.get(id)) {
        this.btnTouchIds.delete(id);
        this.buttonStates[id] = false;
        this.buttonEls.get(id)?.classList.remove(`${CSS_PREFIX}-btn--active`);
        break;
      }
    }
  }

  private resetAllButtons(): void {
    for (const id of Object.keys(this.buttonStates)) {
      this.buttonStates[id] = false;
      this.buttonEls.get(id)?.classList.remove(`${CSS_PREFIX}-btn--active`);
    }
    this.btnTouchIds.clear();
  }

  // ─── CSS generation ──────────────────────────────────────

  private buildCSS(opacity: number, buttons: TouchButton[]): string {
    const r = this.radius;
    const baseSize = r * 2.2; // visual base circle
    const thumbSize = r * 0.75;

    return `
/* TouchController — auto-generated, scoped with "${CSS_PREFIX}-" prefix */

.${CSS_PREFIX}-container {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
  z-index: 50;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

/* ── Joystick zone ── */

.${CSS_PREFIX}-joy-zone {
  position: absolute;
  bottom: 0;
  width: 50%;
  height: 55%;
  pointer-events: auto;
  touch-action: none;
}
.${CSS_PREFIX}-joy-zone--left  { left: 0; }
.${CSS_PREFIX}-joy-zone--right { right: 0; }

.${CSS_PREFIX}-joy-base {
  position: fixed;
  width: ${baseSize}px;
  height: ${baseSize}px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, ${opacity * 0.5});
  transform: translate(-50%, -50%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.1s;
}

.${CSS_PREFIX}-joy-base--active {
  opacity: 1;
}

.${CSS_PREFIX}-joy-thumb {
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${thumbSize}px;
  height: ${thumbSize}px;
  border-radius: 50%;
  background: rgba(255, 255, 255, ${opacity});
  border: 2px solid rgba(255, 255, 255, ${opacity + 0.2});
  transform: translate(-50%, -50%);
  pointer-events: none;
}

/* ── Buttons ── */

.${CSS_PREFIX}-btn-container {
  position: absolute;
  bottom: 28px;
  display: flex;
  flex-direction: column-reverse;
  gap: 14px;
  pointer-events: auto;
  touch-action: none;
}
.${CSS_PREFIX}-btn-container--right { right: 24px; }
.${CSS_PREFIX}-btn-container--left  { left: 24px; }

.${CSS_PREFIX}-btn {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  border: 2.5px solid rgba(255, 255, 255, ${opacity});
  color: rgba(255, 255, 255, ${opacity + 0.15});
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-shadow: 0 1px 3px rgba(0,0,0,0.6);
  transition: transform 0.08s, background 0.08s;
}

.${CSS_PREFIX}-btn--active {
  transform: scale(0.9);
  background: rgba(255, 255, 255, 0.18);
}
`;
  }
}
